
import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useBrand } from '../hooks/useBrand'
import { StarDisplay as _StarDisplay } from '../components/StarRating' // eslint-disable-line

// ── Brand tokens ──
const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'
const TEAL_D = '#1BA888'

// ── Category groups ──
const CATEGORY_GROUPS = [
  { key: 'fashion',     label: 'Fashion & Clothing',    icon: 'M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.57a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.57a2 2 0 00-1.34-2.23z',
    cats: ['Sarees','Clothing','Ethnic wear','Kurtis & Tops','Footwear'] },
  { key: 'accessories', label: 'Accessories & Beauty',  icon: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
    cats: ['Jewellery','Accessories','Bags & Purses','Beauty & Skincare','Haircare'] },
  { key: 'food',        label: 'Food & Organic',        icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    cats: ['Food & Homemade','Bakery & Sweets','Organic & Natural'] },
  { key: 'home',        label: 'Home & Lifestyle',      icon: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z',
    cats: ['Home & Lifestyle','Plants & Gardening','Handicrafts','Pottery & Ceramics','Art & Paintings'] },
  { key: 'electronics', label: 'Electronics & Tech',    icon: 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v11',
    cats: ['Electronics','Mobile accessories'] },
  { key: 'kids',        label: 'Kids, Sports & Pets',   icon: 'M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z',
    cats: ['Kids & Baby','Toys & Games','Sports & Fitness','Pet products'] },
  { key: 'education',   label: 'Education & Services',  icon: 'M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z',
    cats: ['Books & Stationery','Education & Courses','Photography','Other'] },
]

const BD_DISTRICTS = ['All','Dhaka','Chittagong','Rajshahi','Khulna','Barisal','Sylhet','Rangpur','Mymensingh','Comilla','Gazipur','Narayanganj','Cumilla','Jessore','Bogra','Dinajpur','Pabna','Tangail','Sirajganj','Noakhali','Feni','Brahmanbaria','Cox\'s Bazar','Chandpur','Lakshmipur','Manikganj','Narsingdi','Kishoreganj','Sherpur','Netrokona','Jamalpur','Mymensingh','Khagrachhari','Rangamati','Bandarban','Satkhira','Bagerhat','Narail','Magura','Jhenaidah','Kushtia','Meherpur','Chuadanga','Rajbari','Faridpur','Gopalganj','Madaripur','Shariatpur','Munshiganj','Sunamganj','Moulvibazar','Habiganj','Gaibandha','Thakurgaon','Panchagarh','Nilphamari','Lalmonirhat','Kurigram','Joypurhat','Chapai Nawabganj','Natore','Nawabganj','Sirajgonj']
const INT_COUNTRIES = ['All','Australia','United Kingdom','United States','Canada','Germany','Portugal','Italy','New Zealand','Finland','Spain','UAE','Singapore','Malaysia','France','Netherlands','Sweden','Norway','Denmark','Switzerland','Japan','South Korea','India']

const PRICE_RANGES = [
  { key: 'all',     label: 'Any price' },
  { key: 'budget',  label: 'Budget' },
  { key: 'mid',     label: 'Mid-range' },
  { key: 'premium', label: 'Premium' },
  { key: 'luxury',  label: 'Luxury' },
]

const SORT_OPTIONS = [
  { key: 'name',    label: 'A–Z' },
  { key: 'rating',  label: 'Top rated' },
  { key: 'newest',  label: 'Newest' },
  { key: 'reviews', label: 'Most reviewed' },
]

// ── Helpers ──
function getCategories(s) {
  try { const bt = s.business_types ? JSON.parse(s.business_types) : null; if (bt?.length) return bt } catch {}
  return s.category ? [s.category] : []
}
function getUsername(s) { return s?.facebook_username || s?.facebook_url?.split('/').filter(Boolean).pop() || '' }
function waLink(phone) { return phone ? 'https://wa.me/' + phone.replace(/\D/g, '') : null }

// ── Save button ──
function SaveButton({ sellerId, saved, onToggle, size = 18 }) {
  return (
    <button onClick={e => { e.preventDefault(); e.stopPropagation(); onToggle(sellerId) }}
      title={saved ? 'Remove from saved' : 'Save seller'}
      style={{ background: saved ? '#FEF2F2' : 'rgba(255,255,255,0.9)', border: '1px solid', borderColor: saved ? '#FECACA' : '#E5E7EB', borderRadius: '50%', width: size + 14 + 'px', height: size + 14 + 'px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill={saved ? '#EF4444' : 'none'} stroke={saved ? '#EF4444' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
      </svg>
    </button>
  )
}

export default function SellerDirectory() {
  const { brand }     = useBrand()
  const navigate      = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [sellers, setSellers]         = useState([])
  const [loading, setLoading]         = useState(true)
  const [sellerCount, setSellerCount] = useState(0)
  const [search, setSearch]           = useState(searchParams.get('q') || '')
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [region, setRegion]           = useState(searchParams.get('region') || 'all')
  const [location, setLocation]       = useState(searchParams.get('loc') || 'All')
  const [activeGroup, setActiveGroup] = useState(searchParams.get('cat') || 'all')
  const [activeSubcat, setActiveSubcat] = useState(searchParams.get('sub') || '')
  const [priceRange, setPriceRange]   = useState('all')
  const [sortBy, setSortBy]           = useState('name')
  const [viewMode, setViewMode]       = useState('grid')
  const [visibleCount, setVisibleCount] = useState(48)
  const [savedSellers, setSavedSellers] = useState(new Set())
  const [customerAccount, setCustomerAccount] = useState(null)
  const [recentlyViewed, setRecentlyViewed] = useState([])
  const [expandedGroups, setExpandedGroups] = useState(new Set(['fashion']))
  const searchRef = useRef(null)

  useEffect(() => {
    fetchSellers()
    checkCustomerAuth()
    loadRecentlyViewed()
  }, []) // eslint-disable-line

  // Sync URL params
  useEffect(() => {
    const params = {}
    if (search)       params.q      = search
    if (region !== 'all') params.region = region
    if (location !== 'All') params.loc = location
    if (activeGroup !== 'all') params.cat = activeGroup
    if (activeSubcat) params.sub = activeSubcat
    setSearchParams(params, { replace: true })
  }, [search, region, location, activeGroup, activeSubcat]) // eslint-disable-line

  const fetchSellers = async () => {
    setLoading(true)
    const { data, count } = await supabase.from('sellers')
      .select('id,shop_name,business_types,category,district,country,seller_mode,facebook_url,facebook_username,logo_url,phone,tagline,verification_status,about_short,avg_rating,review_count,follower_count,price_range,delivery_coverage,featured_category,member_since,created_at', { count: 'exact' })
      .eq('verification_status', 'verified').order('shop_name')
    setSellers(data || [])
    setSellerCount(count || 0)
    setLoading(false)
  }

  const checkCustomerAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data: acc } = await supabase.from('customer_accounts').select('id').eq('user_id', user.id).single()
    if (!acc) return
    setCustomerAccount(acc)
    const { data: follows } = await supabase.from('seller_followers').select('seller_id').eq('customer_account_id', acc.id)
    setSavedSellers(new Set(follows?.map(f => f.seller_id) || []))
  }

  const loadRecentlyViewed = () => {
    try {
      const rv = JSON.parse(localStorage.getItem('pc_recent_sellers') || '[]')
      setRecentlyViewed(rv)
    } catch {}
  }

  const handleToggleSave = async (sellerId) => {
    if (!customerAccount) { navigate('/customer/login'); return }
    const isSaved = savedSellers.has(sellerId)
    if (isSaved) {
      await supabase.from('seller_followers').delete().eq('seller_id', sellerId).eq('customer_account_id', customerAccount.id)
      setSavedSellers(prev => { const n = new Set(prev); n.delete(sellerId); return n })
    } else {
      await supabase.from('seller_followers').insert({ seller_id: sellerId, customer_account_id: customerAccount.id })
      setSavedSellers(prev => new Set([...prev, sellerId]))
    }
  }

  // ── Filtering ──
  const getCatFilter = () => {
    if (activeSubcat) return [activeSubcat]
    if (activeGroup === 'all') return null
    return CATEGORY_GROUPS.find(g => g.key === activeGroup)?.cats || null
  }

  const filtered = sellers.filter(s => {
    const cats = getCategories(s)
    const q = search.toLowerCase()
    const matchSearch  = !q || s.shop_name?.toLowerCase().includes(q) || s.tagline?.toLowerCase().includes(q) || s.about_short?.toLowerCase().includes(q) || cats.some(c => c.toLowerCase().includes(q)) || s.district?.toLowerCase().includes(q) || s.country?.toLowerCase().includes(q)
    const catFilter = getCatFilter()
    const matchCat    = !catFilter || cats.some(c => catFilter.includes(c))
    const matchRegion = region === 'all' || (region === 'bangladesh' && s.seller_mode !== 'international') || (region === 'international' && s.seller_mode === 'international')
    const matchLoc    = location === 'All' || s.district === location || s.country === location
    const matchPrice  = priceRange === 'all' || s.price_range === priceRange
    return matchSearch && matchCat && matchRegion && matchLoc && matchPrice
  })

  // ── Sorting ──
  const sorted = [...filtered].sort((a, b) => {
    if (a.featured_category && !b.featured_category) return -1
    if (!a.featured_category && b.featured_category) return 1
    if (sortBy === 'rating')  return (b.avg_rating || 0) - (a.avg_rating || 0)
    if (sortBy === 'reviews') return (b.review_count || 0) - (a.review_count || 0)
    if (sortBy === 'newest')  return new Date(b.created_at) - new Date(a.created_at)
    return (a.shop_name || '').localeCompare(b.shop_name || '')
  })

  // ── Category counts ──
  const getCatCount = (catKeys) => sellers.filter(s => {
    const cats = getCategories(s)
    if (region !== 'all') {
      if (region === 'bangladesh' && s.seller_mode === 'international') return false
      if (region === 'international' && s.seller_mode !== 'international') return false
    }
    return !catKeys || cats.some(c => catKeys.includes(c))
  }).length

  // ── Search suggestions ──
  const handleSearchInput = (val) => {
    setSearchInput(val)
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return }
    const q = val.toLowerCase()
    const nameSugs = sellers.filter(s => s.shop_name?.toLowerCase().includes(q)).slice(0, 4).map(s => ({ type: 'seller', label: s.shop_name, id: s.id, username: getUsername(s) }))
    const catSugs  = CATEGORY_GROUPS.flatMap(g => g.cats).filter(c => c.toLowerCase().includes(q)).slice(0, 3).map(c => ({ type: 'category', label: c }))
    setSuggestions([...nameSugs, ...catSugs].slice(0, 6))
    setShowSuggestions(true)
  }

  const handleSearchSubmit = (val) => {
    setSearch(val || searchInput); setSearchInput(val || searchInput); setShowSuggestions(false)
  }

  const handleSuggestionClick = (sug) => {
    if (sug.type === 'seller') {
      setShowSuggestions(false)
      navigate('/sellers/' + (sug.username || sug.id))
    } else {
      setSearch(sug.label); setSearchInput(sug.label); setShowSuggestions(false)
    }
  }

  const locationOptions = region === 'international' ? INT_COUNTRIES : BD_DISTRICTS
  const recentSellers = recentlyViewed.map(id => sellers.find(s => s.id === id)).filter(Boolean).slice(0, 6)

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>
      <style>{`
        .sd-card { background: #fff; border: 1px solid #E5E7EB; transition: all 0.15s; text-decoration: none; display: block; }
        .sd-card:hover { border-color: ${TEAL}; box-shadow: 0 4px 16px rgba(11,31,58,0.08); transform: translateY(-1px); }
        .sd-card-list { background: #fff; border: 1px solid #E5E7EB; border-radius: 12px; transition: all 0.15s; text-decoration: none; display: flex; align-items: center; gap: 14px; padding: 12px 16px; }
        .sd-card-list:hover { border-color: ${TEAL}; box-shadow: 0 2px 8px rgba(11,31,58,0.06); }
        .sd-inp:focus { border-color: ${TEAL} !important; outline: none; }
        .sd-pill { padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid #E5E7EB; background: #fff; color: #6B7280; transition: all 0.12s; font-family: inherit; white-space: nowrap; }
        .sd-pill:hover { border-color: ${NAVY}; color: ${NAVY}; }
        .sd-pill-active { background: ${NAVY} !important; color: #fff !important; border-color: ${NAVY} !important; }
        .sd-pill-teal { background: ${TEAL} !important; color: ${NAVY} !important; border-color: ${TEAL} !important; }
        @keyframes sd-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .sd-fade { animation: sd-fade 0.3s ease-out both; }
        @media(max-width:1024px) { .sd-layout { grid-template-columns: 1fr !important; } .sd-sidebar { display: none !important; } .sd-mobile-cats { display: flex !important; } }
        @media(max-width:768px) { .sd-grid-cards { grid-template-columns: repeat(3,1fr) !important; } }
        @media(max-width:480px) { .sd-grid-cards { grid-template-columns: repeat(2,1fr) !important; } .sd-stat-row { flex-direction: column; gap: 8px !important; } }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ background: NAVY, padding: '0 24px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 2px 12px rgba(11,31,58,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {brand.logo_light_url
            ? <img src={brand.logo_light_url} alt={brand.name} style={{ height: '26px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            : <div style={{ fontSize: '18px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>{brand.name || 'PageConnect'}</div>
          }
          <div style={{ fontSize: '11px', color: TEAL, background: 'rgba(45,212,176,0.12)', padding: '3px 10px', borderRadius: '20px', fontWeight: 700, border: '1px solid rgba(45,212,176,0.25)' }}>Seller Directory</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Link to="/track" style={{ fontSize: '12.5px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }}>Track orders</Link>
          <Link to="/customer/portal" style={{ fontSize: '12.5px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }}>My account</Link>
          <a href="/signup" style={{ fontSize: '12.5px', fontWeight: 700, color: NAVY, textDecoration: 'none', padding: '6px 14px', borderRadius: '8px', background: TEAL }}>List your shop</a>
        </div>
      </nav>

      {/* ── SEARCH HERO ── */}
      <div style={{ background: NAVY, padding: '32px 24px 48px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(45,212,176,0.12)', border: '1px solid rgba(45,212,176,0.25)', borderRadius: '20px', padding: '4px 12px', marginBottom: '16px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize: '11.5px', color: TEAL, fontWeight: 700, letterSpacing: '0.06em' }}>VERIFIED SELLER DIRECTORY</span>
          </div>
          <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#fff', lineHeight: 1.15, letterSpacing: '-0.8px', marginBottom: '8px' }}>
            Find trusted sellers.<br />Shop with confidence.
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', marginBottom: '24px', lineHeight: 1.6 }}>
            Every seller is manually identity-verified. Real businesses, real people.
          </p>

          {/* Search bar */}
          <div style={{ position: 'relative', marginBottom: '16px' }} ref={searchRef}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={searchInput} onChange={e => handleSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
              onFocus={() => searchInput.length >= 2 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="Search sellers, categories, locations..."
              className="sd-inp"
              style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.15)', borderRadius: '14px', padding: '14px 50px 14px 48px', fontSize: '15px', color: '#fff', fontFamily: 'inherit', boxSizing: 'border-box', backdropFilter: 'blur(8px)', WebkitAppearance: 'none' }} />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setSearch(''); setSuggestions([]); }}
                style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: '14px' }}>×</button>
            )}
            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 100, marginTop: '6px', overflow: 'hidden' }}>
                {suggestions.map((sug, i) => (
                  <button key={i} onClick={() => handleSuggestionClick(sug)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', borderBottom: i < suggestions.length - 1 ? '1px solid #F3F4F6' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
                      {sug.type === 'seller' ? <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8z"/> : <circle cx="11" cy="11" r="8"/>}
                    </svg>
                    <span style={{ fontSize: '13px', color: '#111827', fontWeight: 500 }}>{sug.label}</span>
                    <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: 'auto' }}>{sug.type === 'seller' ? 'Shop' : 'Category'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Region tabs */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {[{k:'all',l:'🌐 All sellers'},{k:'bangladesh',l:'🇧🇩 Bangladesh'},{k:'international',l:'🌍 International'}].map(r => (
              <button key={r.k} onClick={() => { setRegion(r.k); setLocation('All') }}
                className={'sd-pill' + (region === r.k ? ' sd-pill-active' : '')}>
                {r.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── TRUST STRIP ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '10px 24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }} className="sd-stat-row">
          {[
            { label: `${sellerCount} verified sellers`, icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Identity manually checked', icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
            { label: 'Free to browse', icon: 'M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' },
            { label: 'Save sellers you love', icon: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round"><path d={s.icon}/></svg>
              <span style={{ fontSize: '12.5px', color: '#374151', fontWeight: 500 }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>

        {/* Recently viewed */}
        {recentSellers.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>Recently viewed</div>
            <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '4px' }}>
              {recentSellers.map(s => (
                <Link key={s.id} to={'/sellers/' + (getUsername(s) || s.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', background: '#fff', borderRadius: '10px', border: '1px solid #E5E7EB', textDecoration: 'none', flexShrink: 0 }}>
                  {s.logo_url
                    ? <img src={s.logo_url} alt="" style={{ width: '24px', height: '24px', borderRadius: '6px', objectFit: 'cover' }} />
                    : <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(45,212,176,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>🏪</div>
                  }
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{s.shop_name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Mobile category dropdown */}
        <div className="sd-mobile-cats" style={{ display: 'none', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <select value={activeGroup} onChange={e => { setActiveGroup(e.target.value); setActiveSubcat('') }}
            style={{ padding: '9px 14px', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '13px', fontFamily: 'inherit', background: '#fff', color: '#111827', outline: 'none', cursor: 'pointer' }}>
            <option value="all">All categories</option>
            {CATEGORY_GROUPS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
          <select value={location} onChange={e => setLocation(e.target.value)}
            style={{ padding: '9px 14px', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '13px', fontFamily: 'inherit', background: '#fff', color: '#111827', outline: 'none', cursor: 'pointer' }}>
            {locationOptions.map(l => <option key={l}>{l}</option>)}
          </select>
        </div>

        <div className="sd-layout" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '20px', alignItems: 'start' }}>

          {/* ── SIDEBAR ── */}
          <div className="sd-sidebar" style={{ position: 'sticky', top: '70px' }}>
            <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>

              {/* All sellers */}
              <button onClick={() => { setActiveGroup('all'); setActiveSubcat('') }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: activeGroup === 'all' ? NAVY : 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', fontFamily: 'inherit' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: activeGroup === 'all' ? '#fff' : '#111827' }}>All sellers</span>
                <span style={{ fontSize: '11.5px', fontWeight: 600, color: activeGroup === 'all' ? 'rgba(255,255,255,0.6)' : '#9CA3AF' }}>{getCatCount(null)}</span>
              </button>

              {/* Category groups */}
              {CATEGORY_GROUPS.map(group => {
                const isActive = activeGroup === group.key
                const isExpanded = expandedGroups.has(group.key)
                return (
                  <div key={group.key} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <button onClick={() => {
                      setActiveGroup(group.key); setActiveSubcat('')
                      setExpandedGroups(prev => { const n = new Set(prev); isExpanded ? n.delete(group.key) : n.add(group.key); return n })
                    }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: isActive && !activeSubcat ? '#F0FDF4' : 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isActive ? TEAL_D : '#9CA3AF'} strokeWidth="2" strokeLinecap="round"><path d={group.icon}/></svg>
                      <span style={{ fontSize: '12.5px', fontWeight: 600, color: isActive ? TEAL_D : '#374151', flex: 1, textAlign: 'left' }}>{group.label}</span>
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{getCatCount(group.cats)}</span>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {isExpanded && (
                      <div style={{ paddingBottom: '6px' }}>
                        {group.cats.map(cat => (
                          <button key={cat} onClick={() => { setActiveGroup(group.key); setActiveSubcat(cat) }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 16px 6px 36px', background: activeSubcat === cat ? 'rgba(45,212,176,0.08)' : 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                            <span style={{ fontSize: '12px', color: activeSubcat === cat ? TEAL_D : '#6B7280', fontWeight: activeSubcat === cat ? 600 : 400 }}>{cat}</span>
                            <span style={{ fontSize: '10.5px', color: '#D1D5DB' }}>
                              {sellers.filter(s => getCategories(s).includes(cat) && (region === 'all' || (region === 'bangladesh' ? s.seller_mode !== 'international' : s.seller_mode === 'international'))).length}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Price range */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid #F3F4F6' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>Price range</div>
                {PRICE_RANGES.map(p => (
                  <button key={p.key} onClick={() => setPriceRange(p.key)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <span style={{ fontSize: '12px', color: priceRange === p.key ? TEAL_D : '#6B7280', fontWeight: priceRange === p.key ? 600 : 400 }}>{p.label}</span>
                    {priceRange === p.key && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  </button>
                ))}
              </div>

              {/* Location filter */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid #F3F4F6' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>Location</div>
                <select value={location} onChange={e => setLocation(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12.5px', fontFamily: 'inherit', background: '#fff', color: '#111827', outline: 'none', cursor: 'pointer' }}>
                  {locationOptions.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ── MAIN GRID ── */}
          <div>
            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                <strong style={{ color: '#111827' }}>{sorted.length}</strong> seller{sorted.length !== 1 ? 's' : ''} found
                {(activeSubcat || activeGroup !== 'all') && <span style={{ color: TEAL_D, fontWeight: 600 }}> · {activeSubcat || CATEGORY_GROUPS.find(g => g.key === activeGroup)?.label}</span>}
                {search && <span style={{ color: '#9CA3AF' }}> · "{search}"</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Sort */}
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px', fontFamily: 'inherit', background: '#fff', color: '#374151', outline: 'none', cursor: 'pointer' }}>
                  {SORT_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                {/* View mode */}
                {[{k:'grid',p:'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z'},{k:'list',p:'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01'}].map(v => (
                  <button key={v.k} onClick={() => setViewMode(v.k)}
                    style={{ padding: '7px', borderRadius: '8px', border: '1px solid', borderColor: viewMode === v.k ? NAVY : '#E5E7EB', background: viewMode === v.k ? NAVY : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={viewMode === v.k ? '#fff' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round"><path d={v.p}/></svg>
                  </button>
                ))}
              </div>
            </div>

            {/* Active filters strip */}
            {(search || region !== 'all' || location !== 'All' || priceRange !== 'all') && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {search     && <FilterTag label={`"${search}"`}     onRemove={() => { setSearch(''); setSearchInput('') }} />}
                {region !== 'all' && <FilterTag label={region === 'bangladesh' ? '🇧🇩 Bangladesh' : '🌍 International'} onRemove={() => { setRegion('all'); setLocation('All') }} />}
                {location !== 'All' && <FilterTag label={location} onRemove={() => setLocation('All')} />}
                {priceRange !== 'all' && <FilterTag label={PRICE_RANGES.find(p => p.key === priceRange)?.label} onRemove={() => setPriceRange('all')} />}
              </div>
            )}

            {loading ? (
              <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '48px', textAlign: 'center' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '3px solid ' + TEAL, borderTopColor: 'transparent', animation: 'sd-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
                <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Loading verified sellers...</div>
              </div>
            ) : sorted.length === 0 ? (
              <EmptyState search={search} group={activeGroup} onClear={() => { setSearch(''); setSearchInput(''); setActiveGroup('all'); setActiveSubcat(''); setPriceRange('all'); setLocation('All') }} />
            ) : viewMode === 'grid' ? (
              <>
                <div className="sd-grid-cards sd-fade" style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: '10px', marginBottom: '16px' }}>
                  {sorted.slice(0, visibleCount).map(seller => <SellerCard key={seller.id} seller={seller} saved={savedSellers.has(seller.id)} onToggleSave={handleToggleSave} />)}
                </div>
                {visibleCount < sorted.length && (
                  <button onClick={() => setVisibleCount(v => v + 48)}
                    style={{ width: '100%', background: '#fff', border: '1px solid #E5E7EB', color: NAVY, padding: '13px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: '16px' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = TEAL}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}>
                    Show more sellers ({sorted.length - visibleCount} remaining)
                  </button>
                )}
              </>
            ) : (
              <div className="sd-fade" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {sorted.slice(0, visibleCount).map(seller => <SellerListRow key={seller.id} seller={seller} saved={savedSellers.has(seller.id)} onToggleSave={handleToggleSave} />)}
                {visibleCount < sorted.length && (
                  <button onClick={() => setVisibleCount(v => v + 48)}
                    style={{ background: '#fff', border: '1px solid #E5E7EB', color: NAVY, padding: '13px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = TEAL}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}>
                    Show more ({sorted.length - visibleCount} remaining)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes sd-spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}

// ── Seller card — grid view (compact, logo-first) ──
function SellerCard({ seller, saved, onToggleSave }) {
  const username = getUsername(seller)
  const href     = '/sellers/' + (username || seller.id)
  const cats     = getCategories(seller)

  const trackView = () => {
    try {
      const rv = JSON.parse(localStorage.getItem('pc_recent_sellers') || '[]')
      const updated = [seller.id, ...rv.filter(id => id !== seller.id)].slice(0, 10)
      localStorage.setItem('pc_recent_sellers', JSON.stringify(updated))
    } catch {}
  }

  return (
    <div style={{ position: 'relative' }}>
      <Link to={href} onClick={trackView} className="sd-card" style={{ borderRadius: '12px', overflow: 'hidden', display: 'block', textDecoration: 'none' }}>
        {/* Featured band */}
        {seller.featured_category && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(to right, ' + TEAL + ', #6366F1)', zIndex: 1 }} />
        )}
        {/* Logo */}
        <div style={{ width: '100%', aspectRatio: '1/1', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
          {seller.logo_url
            ? <img src={seller.logo_url} alt={seller.shop_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(45,212,176,0.08), rgba(11,31,58,0.04))' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(45,212,176,0.4)" strokeWidth="1.2" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18"/></svg>
              </div>
          }
          {/* Rating overlay */}
          {seller.avg_rating > 0 && (
            <div style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(0,0,0,0.65)', borderRadius: '6px', padding: '2px 6px', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff' }}>{Number(seller.avg_rating).toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ padding: '8px 10px 10px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: '3px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {seller.shop_name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize: '10px', fontWeight: 700, color: TEAL_D }}>Verified</span>
          </div>
          {cats.length > 0 && (
            <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cats.slice(0,2).join(' · ')}</div>
          )}
        </div>
      </Link>

      {/* Save button — absolute top right */}
      <div style={{ position: 'absolute', top: '6px', right: '6px', zIndex: 2 }}>
        <SaveButton sellerId={seller.id} saved={saved} onToggle={onToggleSave} size={13} />
      </div>
    </div>
  )
}

// ── Seller row — list view ──
function SellerListRow({ seller, saved, onToggleSave }) {
  const username = getUsername(seller)
  const href     = '/sellers/' + (username || seller.id)
  const cats     = getCategories(seller)

  return (
    <div style={{ position: 'relative' }}>
      <Link to={href} className="sd-card-list" style={{ color: 'inherit' }}>
        {/* Logo */}
        <div style={{ width: '52px', height: '52px', borderRadius: '10px', overflow: 'hidden', background: '#F8FAFC', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {seller.logo_url
            ? <img src={seller.logo_url} alt={seller.shop_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(45,212,176,0.4)" strokeWidth="1.5" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18"/></svg>
          }
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#111827' }}>{seller.shop_name}</span>
            <span style={{ fontSize: '10.5px', fontWeight: 700, color: TEAL_D, display: 'flex', alignItems: 'center', gap: '3px' }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              Verified
            </span>
            {seller.avg_rating > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11.5px', color: '#6B7280' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                {Number(seller.avg_rating).toFixed(1)} ({seller.review_count})
              </span>
            )}
          </div>
          {(seller.tagline || seller.about_short) && (
            <div style={{ fontSize: '12.5px', color: '#6B7280', marginBottom: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '400px' }}>
              {seller.tagline || seller.about_short}
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {cats.slice(0, 3).map(c => (
              <span key={c} style={{ fontSize: '10.5px', fontWeight: 600, color: '#374151', background: '#F3F4F6', padding: '2px 8px', borderRadius: '10px' }}>{c}</span>
            ))}
            <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: '4px' }}>
              📍 {seller.seller_mode === 'international' ? (seller.country || 'International') : (seller.district || 'Bangladesh')}
            </span>
          </div>
        </div>

        {/* Right side */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
          {waLink(seller.phone) && (
            <a href={waLink(seller.phone)} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '8px', background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: '11.5px', fontWeight: 600, color: '#15803D', textDecoration: 'none' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#15803D"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
          )}
          <SaveButton sellerId={seller.id} saved={saved} onToggle={onToggleSave} size={14} />
        </div>
      </Link>
    </div>
  )
}

function FilterTag({ label, onRemove }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 10px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '20px', fontSize: '12px', fontWeight: 600, color: '#15803D' }}>
      {label}
      <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: '14px', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center' }}>×</button>
    </div>
  )
}

function EmptyState({ search, group, onClear }) {
  return (
    <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '48px 24px', textAlign: 'center' }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.2" strokeLinecap="round" style={{ margin: '0 auto 16px', display: 'block' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
        {search ? `No sellers found for "${search}"` : 'No sellers in this category yet'}
      </div>
      <div style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '20px', lineHeight: 1.6 }}>
        {search ? 'Try a different search term or browse categories' : 'Be the first — list your shop for free'}
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={onClear} style={{ padding: '9px 18px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#fff', fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}>Clear filters</button>
        <a href="/signup" style={{ padding: '9px 18px', borderRadius: '10px', border: 'none', background: NAVY, fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer', textDecoration: 'none' }}>List your shop free</a>
      </div>
    </div>
  )
}